// Drives /academy/courses/new. Simple-mode courses with a picked
// video POST metadata + file size, get back a CF Stream tus URL,
// and run tus directly from the browser. Notes-only simple courses
// and structured courses fall through to the regular form path.

import {
  tusUpload,
  formatBytes,
  throttleByIntegerPercent,
} from "./presentation_tus.js";
import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-course-create]";

export function initAcademyCourseCreate(root = document) {
  if (!root.querySelectorAll) return;
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireWidget);
}

function wireWidget(widget) {
  if (!firstBind(widget)) return;

  const form = widget.querySelector("[data-course-create-form]");
  if (!form) return;

  const errorEl = widget.querySelector("[data-course-create-error]");
  const submitBtn = widget.querySelector("[data-course-submit]");
  const simpleSection = widget.querySelector("[data-course-simple-section]");
  const fileInput = widget.querySelector("[data-course-file-input]");
  const dropzone = widget.querySelector("[data-course-dropzone]");
  const fileEmpty = widget.querySelector("[data-course-file-empty]");
  const fileSelected = widget.querySelector("[data-course-file-selected]");
  const fileNameEl = widget.querySelector("[data-course-file-name]");
  const fileSizeEl = widget.querySelector("[data-course-file-size]");
  const uploadingEl = widget.querySelector("[data-course-uploading]");
  const progressBar = widget.querySelector("[data-course-progress-bar]");
  const progressMessage = widget.querySelector(
    "[data-course-progress-message]",
  );
  const progressPercent = widget.querySelector(
    "[data-course-progress-percent]",
  );

  widget.querySelectorAll("[data-course-shape]").forEach((radio) => {
    radio.addEventListener("change", () => {
      const simple = radio.value === "simple" && radio.checked;
      if (simpleSection) simpleSection.classList.toggle("hidden", !simple);
    });
  });

  if (fileInput) {
    fileInput.addEventListener("change", () => {
      const file = fileInput.files && fileInput.files[0];
      if (!file) {
        if (fileEmpty) fileEmpty.style.opacity = "1";
        if (fileSelected) fileSelected.style.opacity = "0";
        return;
      }
      if (fileEmpty) fileEmpty.style.opacity = "0";
      if (fileSelected) fileSelected.style.opacity = "1";
      if (fileNameEl) fileNameEl.textContent = file.name;
      if (fileSizeEl) fileSizeEl.textContent = formatBytes(file.size);
    });
  }

  if (dropzone) {
    dropzone.addEventListener("click", () => fileInput && fileInput.click());
    dropzone.addEventListener("dragenter", () => {
      dropzone.dataset.dragging = "true";
    });
    dropzone.addEventListener("dragover", (event) => event.preventDefault());
    dropzone.addEventListener("dragleave", (event) => {
      if (!dropzone.contains(event.relatedTarget)) {
        dropzone.dataset.dragging = "false";
      }
    });
    dropzone.addEventListener("drop", (event) => {
      event.preventDefault();
      dropzone.dataset.dragging = "false";
      const dt = event.dataTransfer;
      if (!dt || !dt.files || dt.files.length === 0 || !fileInput) return;
      fileInput.files = dt.files;
      fileInput.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  form.addEventListener("submit", async (event) => {
    event.preventDefault();

    const shape =
      widget.querySelector("[data-course-shape]:checked")?.value || "simple";
    const file =
      shape === "simple" && fileInput && fileInput.files && fileInput.files[0];

    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value instanceof File) return;
      if (key === "video") return;
      params.append(key, value);
    });
    if (file) params.append("file_size_bytes", String(file.size));

    setSubmitting(submitBtn, true);
    hideError(errorEl);

    let payload;
    try {
      const resp = await fetch("/academy/courses", {
        method: "POST",
        body: params,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!resp.ok) {
        showError(errorEl, await readErrorMessage(resp));
        setSubmitting(submitBtn, false);
        return;
      }
      payload = await resp.json();
    } catch (err) {
      console.error("academy.course.create: metadata POST failed", err);
      showError(errorEl, "Could not create the course. Please try again.");
      setSubmitting(submitBtn, false);
      return;
    }

    if (!payload || !payload.course_id) {
      showError(errorEl, "Server response was missing course details.");
      setSubmitting(submitBtn, false);
      return;
    }

    // Notes-only / structured courses skip tus and navigate straight
    // to the course detail page.
    if (!payload.upload_url || !file) {
      window.location.assign(
        payload.redirect || `/academy/courses/${payload.course_id}`,
      );
      return;
    }

    if (uploadingEl) uploadingEl.classList.remove("hidden");
    setProgress(progressBar, progressPercent, progressMessage, 0, file.size, 0);
    const beforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    const clearGuards = () =>
      window.removeEventListener("beforeunload", beforeUnload);

    tusUpload({
      file,
      uploadURL: payload.upload_url,
      onError: (err) => {
        console.error("academy.course.create: tus upload failed", err);
        clearGuards();
        if (progressMessage) progressMessage.textContent = "Upload paused.";
        showError(
          errorEl,
          "Upload failed. Delete this draft course and try again with a fresh upload.",
        );
        setSubmitting(submitBtn, false);
      },
      onProgress: throttleByIntegerPercent((uploaded, total, pct) => {
        setProgress(
          progressBar,
          progressPercent,
          progressMessage,
          uploaded,
          total,
          pct,
        );
      }),
      onSuccess: () => {
        setProgress(
          progressBar,
          progressPercent,
          progressMessage,
          file.size,
          file.size,
          100,
        );
        if (progressBar) {
          progressBar.classList.remove("bg-brand-500");
          progressBar.classList.add("bg-emerald-500");
        }
        if (progressMessage) {
          progressMessage.textContent =
            "Upload complete. Your video is being processed…";
        }
        clearGuards();
        window.location.assign(
          payload.redirect || `/academy/courses/${payload.course_id}`,
        );
      },
    });
  });
}

function setProgress(bar, percentEl, messageEl, uploaded, total, pct) {
  if (bar) bar.style.width = `${pct}%`;
  if (percentEl) percentEl.textContent = `${pct}%`;
  if (messageEl) {
    messageEl.textContent = `Uploading ${formatBytes(uploaded)} of ${formatBytes(total)}`;
  }
}

function showError(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}

function hideError(el) {
  if (!el) return;
  el.classList.add("hidden");
  el.textContent = "";
}

function setSubmitting(btn, isSubmitting) {
  if (!btn) return;
  btn.disabled = isSubmitting;
  btn.classList.toggle("opacity-60", isSubmitting);
  btn.classList.toggle("cursor-not-allowed", isSubmitting);
}

async function readErrorMessage(resp) {
  try {
    const body = await resp.json();
    if (body && body.error) return body.error;
  } catch (_) {
    // Fall through.
  }
  return `Request failed (${resp.status}).`;
}
